import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog as ScrollableDialog, ScrollableDialogContent, DialogHeader as ScrollableDialogHeader, DialogTitle as ScrollableDialogTitle, DialogDescription as ScrollableDialogDescription, DialogFooter as ScrollableDialogFooter, DialogTrigger as ScrollableDialogTrigger } from '@/components/ui/scrollable-dialog';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { shopifyApi, formatCurrency } from '@/lib/api/services/shopify';
import { API_CONFIG } from '@/lib/api/config';
import { toast } from '@/hooks/use-toast';
import { useCatalogInfo } from '@/hooks/useCommerce';
import { Search, X, Trash2, Loader2, ArrowLeft, RefreshCw, Webhook, Pencil, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { ShopifyOrders } from '@/components/shopify/ShopifyOrders';
import { ShopifyProducts } from '@/components/shopify/ShopifyProducts';
import { CreateShopifyProductDialog } from '@/components/shopify/CreateShopifyProductDialog';
import { ShopifyCustomers } from '@/components/shopify/ShopifyCustomers';
import { ShopifyCheckouts } from '@/components/shopify/ShopifyCheckouts';
import { ApiKeysManager } from '@/components/shopify/ApiKeysManager';
import { ShopifyPlaybooks } from '@/components/shopify/ShopifyPlaybooks';
import { useAuth } from '@/components/AuthProvider';
import { friendlyMessage, showApiError } from '@/lib/errors';

export default function ShopifyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.organization_id || null;
  const { selectedAccountId, isLoading: accountsLoading } = useBusinessAccountContext();
  const { data: catalogInfo } = useCatalogInfo();
  const [localProducts, setLocalProducts] = useState<any[]>([]);
  const [localCheckouts, setLocalCheckouts] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{ shop?: string; currency?: string; installedAt?: string; businessId?: string | null } | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [shopCurrency, setShopCurrency] = useState<string>('USD');
  const [connectionMethod, setConnectionMethod] = useState<'token' | 'credentials'>('credentials');
  const [manualForm, setManualForm] = useState({
    shop: '',
    accessToken: '',
    clientId: '',
    clientSecret: '',
  });
  const [manualLoading, setManualLoading] = useState(false);
  const [createProductOpen, setCreateProductOpen] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({
    title: '',
    body_html: '',
    vendor: '',
    product_type: '',
    tags: '',
    status: 'draft' as 'active' | 'draft',
    images: [] as string[],
    variants: [{ price: '', sku: '', inventory_quantity: 0 as number | string }],
  });
  const [createProductLoading, setCreateProductLoading] = useState(false);
  const [syncProductsLoading, setSyncProductsLoading] = useState(false);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [syncCustomersLoading, setSyncCustomersLoading] = useState(false);
  const [customersRefreshKey, setCustomersRefreshKey] = useState(0);
  const [syncOrdersLoading, setSyncOrdersLoading] = useState(false);
  const [ordersRefreshKey, setOrdersRefreshKey] = useState(0);
  const [syncToCatalogLoading, setSyncToCatalogLoading] = useState(false);
  const [syncCheckoutsLoading, setSyncCheckoutsLoading] = useState(false);
  const [checkoutsRefreshKey, setCheckoutsRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ id: string; shopify_product_id: string; title: string } | null>(null);
  const [deleteProductLoading, setDeleteProductLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Webhook management state
  const [webhooks, setWebhooks] = useState<Array<{ id: number; address: string; topic: string; created_at: string; updated_at: string; format: string; api_version: string }>>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhookCreating, setWebhookCreating] = useState<string | null>(null);
  const [webhookDeleting, setWebhookDeleting] = useState<number | null>(null);
  const [registerAllLoading, setRegisterAllLoading] = useState(false);
  const [editingTopic, setEditingTopic] = useState<string | null>(null);

  // URL State Management
  const activeTab = searchParams.get('tab') || 'connect';
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      // Create fresh params
      const newParams = new URLSearchParams();
      // Keep only 'tab'
      newParams.set('tab', tab);
      return newParams;
    });
  };

  const productsPage = parseInt(searchParams.get('page') || '1');
  const setProductsPage = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const [productsPageSize, setProductsPageSize] = useState(10);
  const [checkoutsPage, setCheckoutsPage] = useState(1);
  const [checkoutsPageSize, setCheckoutsPageSize] = useState(10);

  // Search and filter states for products
  const productsSearch = searchParams.get('search') || '';
  const setProductsSearch = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) newParams.set('search', value);
      else newParams.delete('search');
      newParams.set('page', '1'); // Reset page on search
      return newParams;
    });
  };

  const productsStatusFilter = searchParams.get('status') || 'all';
  const setProductsStatusFilter = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value !== 'all') newParams.set('status', value);
      else newParams.delete('status');
      newParams.set('page', '1');
      return newParams;
    });
  };

  const productsVendorFilter = searchParams.get('vendor') || 'all';
  const setProductsVendorFilter = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value !== 'all') newParams.set('vendor', value);
      else newParams.delete('vendor');
      newParams.set('page', '1');
      return newParams;
    });
  };

  const productsTypeFilter = searchParams.get('type') || 'all';
  const setProductsTypeFilter = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value !== 'all') newParams.set('type', value);
      else newParams.delete('type');
      newParams.set('page', '1');
      return newParams;
    });
  };

  const productsSortBy = searchParams.get('sort') || 'title';
  const setProductsSortBy = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('sort', value);
      return newParams;
    });
  };

  const productsSortOrder = (searchParams.get('order') as 'asc' | 'desc') || 'asc';
  const setProductsSortOrder = (value: 'asc' | 'desc') => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('order', value);
      return newParams;
    });
  };

  // Derived state for products
  const uniqueVendors = useMemo(() => {
    const vendors = new Set(localProducts.map(p => p.vendor).filter(Boolean));
    return Array.from(vendors).sort();
  }, [localProducts]);

  const uniqueTypes = useMemo(() => {
    const types = new Set(localProducts.map(p => p.product_type).filter(Boolean));
    return Array.from(types).sort();
  }, [localProducts]);

  const filteredAndSortedProducts = useMemo(() => {
    let result = [...localProducts];

    // Search
    if (productsSearch) {
      const searchLower = productsSearch.toLowerCase();
      result = result.filter(p =>
        p.title?.toLowerCase().includes(searchLower) ||
        p.vendor?.toLowerCase().includes(searchLower) ||
        p.product_type?.toLowerCase().includes(searchLower)
      );
    }

    // Filters
    if (productsStatusFilter !== 'all') {
      result = result.filter(p => p.status === productsStatusFilter);
    }
    if (productsVendorFilter !== 'all') {
      result = result.filter(p => p.vendor === productsVendorFilter);
    }
    if (productsTypeFilter !== 'all') {
      result = result.filter(p => p.product_type === productsTypeFilter);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (productsSortBy) {
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'price':
          const priceA = parseFloat(a.variants?.[0]?.price || '0');
          const priceB = parseFloat(b.variants?.[0]?.price || '0');
          comparison = priceA - priceB;
          break;
        case 'vendor':
          comparison = (a.vendor || '').localeCompare(b.vendor || '');
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          comparison = 0;
      }
      return productsSortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [localProducts, productsSearch, productsStatusFilter, productsVendorFilter, productsTypeFilter, productsSortBy, productsSortOrder]);

  // Default webhook URL derived from env
  const defaultWebhookUrl = useMemo(() => {
    const base = import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL;
    const origin = base.replace(/\/api\/v1\/?$/, '');
    return `${origin}/api/v1/shopify/webhooks`;
  }, []);
  // Per-topic editable webhook URLs
  const [topicUrls, setTopicUrls] = useState<Record<string, string>>({});

  const SUPPORTED_WEBHOOK_TOPICS = [
    { topic: 'orders/create', label: 'Order Created', description: 'When a new order is placed' },
    { topic: 'orders/updated', label: 'Order Updated', description: 'When an order is modified' },
    { topic: 'orders/paid', label: 'Order Paid', description: 'When payment is confirmed' },
    { topic: 'orders/cancelled', label: 'Order Cancelled', description: 'When an order is cancelled' },
    { topic: 'orders/delete', label: 'Order Deleted', description: 'Syncs order deletions' },
    { topic: 'checkouts/create', label: 'Checkout Created', description: 'Abandoned cart detection' },
    { topic: 'checkouts/update', label: 'Checkout Updated', description: 'Checkout status changes' },
    { topic: 'customers/create', label: 'Customer Created', description: 'When a new customer signs up' },
    { topic: 'orders/fulfilled', label: 'Order Fulfilled', description: 'When an order is fully fulfilled' },
    { topic: 'orders/partially_fulfilled', label: 'Order Partially Fulfilled', description: 'When an order is partially fulfilled' },
    { topic: 'refunds/create', label: 'Refund Created', description: 'When a refund is issued' },
    { topic: 'products/create', label: 'Product Created', description: 'Syncs new products' },
    { topic: 'products/update', label: 'Product Updated', description: 'Syncs product changes' },
    { topic: 'products/delete', label: 'Product Deleted', description: 'Syncs product deletions' },
    { topic: 'app/uninstalled', label: 'App Uninstalled', description: 'Cleanup when app is removed' },
  ];

  const loadWebhooks = async () => {
    if (!selectedAccountId) return;
    setWebhooksLoading(true);
    try {
      const list = await shopifyApi.listWebhooks(selectedAccountId);
      setWebhooks(list);
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to load webhooks' } });
    } finally {
      setWebhooksLoading(false);
    }
  };

  const getTopicUrl = (topic: string) => topicUrls[topic] ?? topicUrls['__default__'] ?? defaultWebhookUrl;
  const setTopicUrl = (topic: string, url: string) => setTopicUrls(prev => ({ ...prev, [topic]: url }));

  const handleCreateWebhook = async (topic: string) => {
    if (!selectedAccountId) return;
    const address = getTopicUrl(topic);
    if (!address) {
      toast({ title: 'Enter a webhook URL first', variant: 'destructive' });
      return;
    }
    setWebhookCreating(topic);
    try {
      await shopifyApi.createWebhook(selectedAccountId, topic, address);
      toast({ variant: "success", title: `Webhook registered: ${topic}` });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to create webhook' } });
    } finally {
      setWebhookCreating(null);
    }
  };

  const handleUpdateWebhook = async (topic: string, webhookId: number) => {
    if (!selectedAccountId) return;
    const address = getTopicUrl(topic);
    if (!address) {
      toast({ title: 'Enter a webhook URL first', variant: 'destructive' });
      return;
    }
    setWebhookCreating(topic);
    try {
      await shopifyApi.updateWebhook(selectedAccountId, webhookId, address);
      toast({ variant: "success", title: `Webhook updated: ${topic}` });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to update webhook' } });
    } finally {
      setWebhookCreating(null);
    }
  };

  const handleDeleteWebhook = async (webhookId: number) => {
    if (!selectedAccountId) return;
    setWebhookDeleting(webhookId);
    try {
      await shopifyApi.deleteWebhook(selectedAccountId, webhookId);
      toast({ variant: "success", title: 'Webhook removed' });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to delete webhook' } });
    } finally {
      setWebhookDeleting(null);
    }
  };

  const handleRegisterAll = async () => {
    if (!selectedAccountId) return;
    const targetUrl = getTopicUrl('__default__');
    if (!targetUrl) {
      toast({ title: 'Enter a webhook URL first', variant: 'destructive' });
      return;
    }
    setRegisterAllLoading(true);
    let count = 0;
    let failed = 0;
    try {
      // Refresh the full list first so we have the latest state
      const allWebhooks = await shopifyApi.listWebhooks(selectedAccountId);

      for (const { topic } of SUPPORTED_WEBHOOK_TOPICS) {
        const existing = allWebhooks.find(w => w.topic === topic);
        try {
          if (!existing) {
            await shopifyApi.createWebhook(selectedAccountId, topic, targetUrl);
            count++;
          } else if (existing.address !== targetUrl) {
            await shopifyApi.deleteWebhook(selectedAccountId, existing.id);
            await shopifyApi.createWebhook(selectedAccountId, topic, targetUrl);
            count++;
          }
        } catch {
          failed++;
        }
      }
      const parts = [];
      if (count) parts.push(`${count} registered`);
      if (failed) parts.push(`${failed} skipped (permission issues)`);
      toast({ title: parts.length ? parts.join(', ') : 'All webhooks already up to date' });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to register webhooks' } });
    } finally {
      setRegisterAllLoading(false);
    }
  };

  // Load webhooks when configuration tab is active
  useEffect(() => {
    if (activeTab === 'configuration' && isConnected && selectedAccountId) {
      loadWebhooks();
    }
  }, [activeTab, isConnected, selectedAccountId]);

  // Seed topicUrls from fetched webhooks so the input shows the current registered address
  useEffect(() => {
    if (webhooks.length > 0) {
      setTopicUrls(prev => {
        const next = { ...prev };
        for (const w of webhooks) {
          if (!next[w.topic]) next[w.topic] = w.address;
        }
        return next;
      });
    }
  }, [webhooks]);

  // Check connection status on mount and when account changes —
  // clear previous BA's cached state first so the UI doesn't briefly
  // render the old account's connection info / products / checkouts.
  useEffect(() => {
    setIsConnected(false);
    setConnectionInfo(null);
    setLocalProducts([]);
    setLocalCheckouts([]);
    setShopCurrency('USD');
    setError(null);
    checkConnection();
  }, [selectedAccountId, organizationId]);

  const checkConnection = async () => {
    setCheckingConnection(true);
    try {
      // Check if connection exists in database (works with or without businessId)
      const connectionStatus = await shopifyApi.checkConnection(selectedAccountId);
      if (connectionStatus.connected) {
        setIsConnected(true);
        setConnectionInfo({
          shop: connectionStatus.shop,
          currency: connectionStatus.currency,
          installedAt: connectionStatus.installedAt,
          businessId: connectionStatus.businessId || null,
        });
        setShopCurrency(connectionStatus.currency || 'USD');

        // Products are loaded by ShopifyProducts component via refreshKey
      } else {
        setIsConnected(false);
        setConnectionInfo(null);
        setLocalProducts([]);
        setShopCurrency('USD');
      }
    } catch (e: any) {
      setIsConnected(false);
      setConnectionInfo(null);
      setLocalProducts([]);
      setShopCurrency('USD');
    } finally {
      setCheckingConnection(false);
    }
  };




  const handleSyncProducts = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required for Syncing',
        description: 'To sync products from Shopify, please select a business account. You can view existing products without one.',
        variant: 'default',
      });
      return;
    }
    setSyncProductsLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncProducts(selectedAccountId);

      // Check if sync was queued (background job)
      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Products sync started in the background. You\'ll get a notification when it finishes.',
        });
      } else {
        // Fallback for synchronous response
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Products Synced', description: `Synced ${count} ${count === 1 ? 'product' : 'products'} from Shopify.` }
          : { title: 'Up to date', description: 'No new products to sync from Shopify.' }
        );
        setProductsPage(1);
        setProductsRefreshKey(k => k + 1);
      }
    } catch (e: any) {
      const msg = e.message || 'Failed to sync products';
      setError(msg);
      showApiError(e, { fallback: { title: 'Sync Failed', description: msg } });
    } finally {
      setSyncProductsLoading(false);
    }
  };

  const handleSyncCustomers = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync customers.',
        variant: 'default',
      });
      return;
    }
    setSyncCustomersLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncCustomers(selectedAccountId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Customers sync started in the background.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Customers Synced', description: `Synced ${count} ${count === 1 ? 'customer' : 'customers'} from Shopify.` }
          : { title: 'Up to date', description: 'No new customers to sync from Shopify.' }
        );
        setCustomersRefreshKey(prev => prev + 1);
      }
    } catch (e: any) {
      const friendly = showApiError(e, { fallback: { title: 'Sync Failed', description: 'Failed to sync customers' } });
      setError(friendly.description);
    } finally {
      setSyncCustomersLoading(false);
    }
  };

  const handleSyncOrders = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync orders.',
        variant: 'default',
      });
      return;
    }
    setSyncOrdersLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncOrders(selectedAccountId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Orders sync started in the background.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Orders Synced', description: `Synced ${count} ${count === 1 ? 'order' : 'orders'} from Shopify.` }
          : { title: 'Up to date', description: 'No new orders to sync from Shopify.' }
        );
        setOrdersRefreshKey(prev => prev + 1);
      }
    } catch (e: any) {
      const friendly = showApiError(e, { fallback: { title: 'Sync Failed', description: 'Failed to sync orders' } });
      setError(friendly.description);
    } finally {
      setSyncOrdersLoading(false);
    }
  };

  const handleSyncToCatalog = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync products to WhatsApp catalog.',
        variant: 'default',
      });
      return;
    }
    if (!isConnected) {
      toast({
        title: 'Shopify Connection Required',
        description: 'Please connect your Shopify store first.',
        variant: 'default',
      });
      return;
    }
    const targetBusinessId = connectionInfo?.businessId || selectedAccountId;
    if (!targetBusinessId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync Shopify products.',
        variant: 'default',
      });
      return;
    }
    const catalogStatusKnown = typeof catalogInfo !== 'undefined';
    if (catalogStatusKnown && (!catalogInfo || catalogInfo.status !== 'connected')) {
      toast({
        title: 'WhatsApp Catalog Not Connected',
        description: 'Please connect a WhatsApp catalog first. Go to the Commerce page to set up your catalog.',
        variant: 'destructive',
      });
      return;
    }
    setSyncToCatalogLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncToCatalog(selectedAccountId, targetBusinessId, {
        limit: 100,
        skipExisting: true,
      });
      toast({ variant: "success",
        title: 'Catalog Sync Started',
        description:
          res?.message ||
          'Sync started in the background. You will receive a notification when it finishes.',
      });
    } catch (e: any) {
      // Try to extract error message from response
      let errorMsg = 'Failed to sync products to WhatsApp catalog';
      if (e.message) {
        errorMsg = e.message;
      } else if (e.response) {
        try {
          const errorData = await e.response.json();
          errorMsg = errorData.message || errorData.error || errorMsg;
        } catch {
          errorMsg = e.response.statusText || errorMsg;
        }
      }

      // Check for specific error types
      if (errorMsg.includes('catalog not connected') || errorMsg.includes('No connected catalog')) {
        errorMsg = 'WhatsApp catalog is not connected. Please go to the Commerce page to set up your catalog.';
      }

      setError(errorMsg);
      showApiError(e, { fallback: { title: 'Sync Failed', description: errorMsg } });
    } finally {
      setSyncToCatalogLoading(false);
    }
  };

  const handleSyncCheckouts = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync checkouts.',
        variant: 'default',
      });
      return;
    }
    setSyncCheckoutsLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncCheckouts(selectedAccountId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Checkouts sync started in the background.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Checkouts Synced', description: `Synced ${count} ${count === 1 ? 'checkout' : 'checkouts'} from Shopify.` }
          : { title: 'Up to date', description: 'No new checkouts to sync from Shopify.' }
        );
        setCheckoutsRefreshKey(prev => prev + 1);
      }
    } catch (e: any) {
      const friendly = showApiError(e, { fallback: { title: 'Sync Failed', description: 'Failed to sync checkouts' } });
      setError(friendly.description);
    } finally {
      setSyncCheckoutsLoading(false);
    }
  };



  const handleCreateProduct = async () => {
    if (!selectedAccountId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to create products in Shopify.',
        variant: 'default',
      });
      return;
    }
    if (!createProductForm.title) {
      setError('Product title is required');
      return;
    }
    // Validate price
    const priceValue = createProductForm.variants[0]?.price?.trim();
    if (!priceValue) {
      setError('Product price is required');
      return;
    }
    const priceNum = parseFloat(priceValue);
    if (isNaN(priceNum) || priceNum < 0) {
      setError('Please enter a valid price (must be a number >= 0)');
      return;
    }
    setCreateProductLoading(true);
    setError(null);
    try {
      // Prepare tags - convert comma-separated string to array
      const tagsArray = createProductForm.tags
        ? createProductForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      // Prepare images - convert URL strings to Shopify image format
      const imagesArray = createProductForm.images
        .filter(url => url.trim())
        .map(url => ({ src: url.trim() }));

      const productData = {
        title: createProductForm.title.trim(),
        body_html: createProductForm.body_html.trim() || undefined,
        vendor: createProductForm.vendor.trim() || undefined,
        product_type: createProductForm.product_type.trim() || undefined,
        tags: tagsArray.length > 0 ? tagsArray.join(', ') : undefined,
        status: createProductForm.status,
        images: imagesArray.length > 0 ? imagesArray : undefined,
        variants: [{
          price: priceValue,
          sku: createProductForm.variants[0]?.sku?.trim() || undefined,
          inventory_quantity: createProductForm.variants[0]?.inventory_quantity || 0,
        }],
      };
      await shopifyApi.createProduct(selectedAccountId, productData);
      toast({ variant: "success",
        title: 'Product Created',
        description: 'Product created successfully in Shopify.',
      });
      setCreateProductOpen(false);
      setCreateProductForm({
        title: '',
        body_html: '',
        vendor: '',
        product_type: '',
        tags: '',
        status: 'draft',
        images: [],
        variants: [{ price: '', sku: '', inventory_quantity: 0 }],
      });
      setProductsPage(1);
      setProductsRefreshKey(k => k + 1);
    } catch (e: any) {
      setError(friendlyMessage(e, 'Failed to create product'));
    } finally {
      setCreateProductLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedAccountId || !productToDelete) {
      return;
    }
    setDeleteProductLoading(true);
    setError(null);
    try {
      await shopifyApi.deleteProduct(selectedAccountId, productToDelete.shopify_product_id);
      toast({ variant: "success",
        title: 'Product Deleted',
        description: 'Product deleted successfully from Shopify.',
      });
      setDeleteProductDialogOpen(false);
      setProductToDelete(null);
      setProductsRefreshKey(k => k + 1);
    } catch (e: any) {
      setError(friendlyMessage(e, 'Failed to delete product'));
    } finally {
      setDeleteProductLoading(false);
    }
  };

  const openDeleteDialog = (product: any) => {
    setProductToDelete({
      id: product.id,
      shopify_product_id: product.shopify_product_id,
      title: product.title || 'Untitled Product',
    });
    setDeleteProductDialogOpen(true);
  };

  // Define columns for Products DataTable
  const productsColumns: Column<typeof filteredAndSortedProducts[0]>[] = useMemo(() => [
    {
      key: 'image',
      header: 'Image',
      render: (_, product) => {
        let images = product.images;
        if (typeof images === 'string') {
          try {
            images = JSON.parse(images);
          } catch (e) {
            images = [];
          }
        }
        if (!Array.isArray(images)) {
          images = [];
        }
        const productImage = images.length > 0 ? images[0].src : null;
        return (
          <>
            {productImage ? (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden bg-muted flex items-center justify-center">
                <img
                  src={productImage}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-muted flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-muted-foreground opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </>
        );
      },
      className: 'w-12 sm:w-16',
    },
    {
      key: 'product',
      header: 'Product',
      render: (_, product) => {
        return (
          <div>
            <div className="font-medium text-xs sm:text-sm truncate max-w-[150px] sm:max-w-none">{product.title || 'Untitled'}</div>
            {product.handle && (
              <div className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-none">/{product.handle}</div>
            )}
          </div>
        );
      },
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'vendor',
      header: 'Vendor',
      render: (_, product) => {
        return <span className="text-xs sm:text-sm text-muted-foreground">{product.vendor || '—'}</span>;
      },
      className: 'text-xs sm:text-sm text-muted-foreground hidden sm:table-cell',
      headerClassName: 'hidden sm:table-cell',
    },
    {
      key: 'type',
      header: 'Type',
      render: (_, product) => {
        return <span className="text-xs sm:text-sm text-muted-foreground">{product.product_type || '—'}</span>;
      },
      className: 'text-xs sm:text-sm text-muted-foreground hidden md:table-cell',
      headerClassName: 'hidden md:table-cell',
    },
    {
      key: 'price',
      header: 'Price',
      render: (_, product) => {
        let variants = product.variants;
        if (typeof variants === 'string') {
          try {
            variants = JSON.parse(variants);
          } catch (e) {
            variants = [];
          }
        }
        if (!Array.isArray(variants)) {
          variants = [];
        }
        const firstVariant = variants.length > 0 ? variants[0] : null;
        let priceValue: number | null = null;
        if (firstVariant?.price) {
          const priceStr = String(firstVariant.price);
          priceValue = parseFloat(priceStr);
          if (isNaN(priceValue)) {
            priceValue = null;
          }
        }
        return (
          <span className="text-right font-medium text-xs sm:text-sm">
            {priceValue !== null ? formatCurrency(priceValue, shopCurrency) : '—'}
          </span>
        );
      },
      className: 'text-right font-medium text-xs sm:text-sm',
      headerClassName: 'text-right',
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, product) => {
        return (
          <Badge variant={product.status === 'active' ? 'default' : 'secondary'} className="text-xs">
            {product.status || 'draft'}
          </Badge>
        );
      },
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'variants',
      header: 'Variants',
      render: (_, product) => {
        let variants = product.variants;
        if (typeof variants === 'string') {
          try {
            variants = JSON.parse(variants);
          } catch (e) {
            variants = [];
          }
        }
        if (!Array.isArray(variants)) {
          variants = [];
        }
        return (
          <span className="text-right text-xs sm:text-sm text-muted-foreground">
            {variants.length > 0 ? variants.length : 0}
          </span>
        );
      },
      className: 'text-right text-xs sm:text-sm text-muted-foreground hidden lg:table-cell',
      headerClassName: 'text-right hidden lg:table-cell',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, product) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDialog(product);
            }}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      className: 'w-16 sm:w-20',
    },
  ], [localProducts, shopCurrency]);


  const handleManualConnect = async () => {
    if (!manualForm.shop) {
      setError('Shop domain is required');
      return;
    }

    if (connectionMethod === 'token' && !manualForm.accessToken) {
      setError('Access token is required');
      return;
    }

    if (connectionMethod === 'credentials' && (!manualForm.clientId || !manualForm.clientSecret)) {
      setError('Client ID and Client Secret are required');
      return;
    }

    setManualLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_CONFIG.BASE_URL}/shopify/manual-install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: selectedAccountId || null,
          shop: manualForm.shop,
          accessToken: connectionMethod === 'token' ? manualForm.accessToken : undefined,
          clientId: connectionMethod === 'credentials' ? manualForm.clientId : undefined,
          clientSecret: connectionMethod === 'credentials' ? manualForm.clientSecret : undefined,
          apiVersion: '2025-01',
          plan: 'monthly',
          shopCurrency: 'USD',
        }),
      });
      const json = await res.json();
      if (!res.ok || !json?.ok) {
        const errorMessage = json?.error || 'Install failed';
        setError(errorMessage);
        toast({
          title: 'Connection Failed',
          description: "Invalid Shopify Credentials.",
          variant: 'destructive',
        });
        return;
      }
      setError(null);
      toast({ variant: "success",
        title: 'Shopify Connected',
        description: "Connected successfully!",
      });
      setManualForm({ shop: '', accessToken: '', clientId: '', clientSecret: '' });
      // Update connection status. Shopify connects via an inline fetch (not the
      // shopifyApi.connect service method), so clear the cached connection check
      // here — otherwise the checkConnection() below would return the stale
      // pre-connect "not connected" result within the cache TTL and overwrite the
      // just-connected state.
      setIsConnected(true);
      setConnectionInfo({ shop: manualForm.shop });
      clearConnectionCheckCache('shopify:');
      await checkConnection();
      // Switch to products tab after successful connection if on connect tab
      if (activeTab === 'connect') {
        setActiveTab('products');
      }
    } catch (e: any) {
      const errorMessage = e.message || 'Failed to connect manually';
      setError(errorMessage);
      showApiError(e, { fallback: { title: 'Connection Failed', description: errorMessage } });
    } finally {
      setManualLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (disconnectLoading) return;
    setDisconnectLoading(true);
    setError(null);
    try {
      await shopifyApi.disconnect(selectedAccountId || connectionInfo?.businessId || null);
      toast({ variant: "success",
        title: 'Shopify Disconnected',
        description: 'The Shopify connection has been removed.',
      });
      setIsConnected(false);
      setConnectionInfo(null);
      setShopCurrency('USD');
      setLocalProducts([]);
      setActiveTab('connect');
    } catch (e: any) {
      setError(friendlyMessage(e, 'Failed to disconnect Shopify'));
    } finally {
      setDisconnectLoading(false);
      setDisconnectDialogOpen(false);
    }
  };

  // Check if business accounts are still loading
  if (accountsLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <Loader2 className="h-12 w-12 mx-auto animate-spin mb-4" />
            <h3 className="text-lg font-semibold mb-2">Loading Business Accounts</h3>
            <p className="text-muted-foreground mb-4">
              Please wait while we load your business accounts...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderTabActions = () => {
    if (!isConnected) {
      return null;
    }

    if (activeTab === 'customers') return (
      <div className="flex items-center gap-2">
        <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncCustomers} disabled={syncCustomersLoading}>
          {syncCustomersLoading ? 'Syncing…' : 'Sync from Shopify'}
        </Button>
      </div>
    );

    if (activeTab === 'orders') {
      return (
        <div className="flex items-center gap-2">
          <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncOrders} disabled={syncOrdersLoading}>
            {syncOrdersLoading ? 'Syncing…' : 'Sync from Shopify'}
          </Button>
        </div>
      );
    }

    if (activeTab === 'products') {
      return (
        <div className="flex items-center gap-2">
          <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncProducts} disabled={syncProductsLoading}>
            {syncProductsLoading ? 'Syncing…' : 'Sync from Shopify'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className='text-xs sm:text-sm'
            onClick={handleSyncToCatalog}
            disabled={syncToCatalogLoading || !selectedAccountId}
          >
            {syncToCatalogLoading ? 'Syncing to Catalog…' : 'Sync to WhatsApp Catalog'}
          </Button>
          <Button variant="default" size="sm" className="text-xs sm:text-sm" onClick={() => setCreateProductOpen(true)}>
            Create Product
          </Button>
          <CreateShopifyProductDialog
            open={createProductOpen}
            onOpenChange={setCreateProductOpen}
            selectedAccountId={selectedAccountId}
            shopCurrency={shopCurrency}
            onSuccess={() => setProductsRefreshKey(k => k + 1)}
          />
        </div>
      );
    }
    // Orders tab actions handled by component
    if (activeTab === 'checkouts') {
      return (
        <div className="flex items-center gap-2">
          <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncCheckouts} disabled={syncCheckoutsLoading}>
            {syncCheckoutsLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Checkouts
              </>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className=" space-y-4 sm:space-y-6">


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home/integrations')}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          <div>
            <CardTitle className="text-xl sm:text-2xl">Shopify Integration</CardTitle>
            <CardDescription className="text-sm">Connect your Shopify shop and manage products & orders</CardDescription>
          </div>
        </div>
        {checkingConnection && (
          <Badge variant="outline">Checking connection...</Badge>
        )}
      </div>


      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col gap-3 sm:flex-row mb-4 sm:items-center sm:justify-between">
          <div className="flex overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex gap-2 p-2 h-[46px] rounded-[20px]">
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="connect">
                <span className="relative z-10">Connect Shop</span>
                {activeTab === 'connect' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="products" disabled={!isConnected}>
                <span className="relative z-10">Products</span>
                {activeTab === 'products' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="customers" disabled={!isConnected}>
                <span className="relative z-10">Customers</span>
                {activeTab === 'customers' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="orders" disabled={!isConnected}>
                <span className="relative z-10">Orders</span>
                {activeTab === 'orders' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="checkouts" disabled={!isConnected}>
                <span className="relative z-10">Abandoned</span>
                {activeTab === 'checkouts' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger
                className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                value="playbooks"
                disabled={!isConnected}
                data-testid="shopify-tab-playbooks"
              >
                <span className="relative z-10">Auto Messages</span>
                {activeTab === 'playbooks' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              {/* <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="api-keys">
                <span className="relative z-10">API Keys</span>
                {activeTab === 'api-keys' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger> */}
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="configuration" disabled={!isConnected}>
                <span className="relative z-10">Configuration</span>
                {activeTab === 'configuration' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
            </TabsList>
          </div>
          {renderTabActions()}
        </div>

        {/* Connect Tab */}
        <TabsContent value="connect" className="space-y-4">
          {isConnected ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/20 p-4 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Connected shop</p>
                    <p className="text-xl font-semibold">{connectionInfo?.shop}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          Disconnect
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Disconnect Shopify</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the current Shopify connection. You can reconnect at any time, but product and order syncing will stop until you do.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={disconnectLoading}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDisconnect}
                            disabled={disconnectLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Connected on</p>
                    <p className="font-medium">
                      {connectionInfo?.installedAt ? new Date(connectionInfo.installedAt).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Currency</p>
                    <p className="font-medium">{connectionInfo?.currency || shopCurrency}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Status</p>
                    <p className="font-medium text-green-600 dark:text-green-400">Connected</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('products')}>
                    Manage Products
                  </Button>
                </div>
              </div>
              {error && (
                <div className="text-xs sm:text-sm text-destructive p-2 sm:p-3 bg-destructive/10 rounded">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-4">Connect Your Shopify Shop</h3>
              <div className="space-y-4">
                {/* Method Toggle */}
                <div className="flex p-1 bg-muted rounded-lg w-fit">
                  <button
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${connectionMethod === 'credentials'
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                    onClick={() => setConnectionMethod('credentials')}
                  >
                    New (Client Credentials)
                  </button>
                  <button
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${connectionMethod === 'token'
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                    onClick={() => setConnectionMethod('token')}
                  >
                    Classic (Access Token)
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shop">Shop Domain *</Label>
                    <Input
                      id="shop"
                      placeholder="cool-store.myshopify.com"
                      value={manualForm.shop}
                      onChange={(e) => setManualForm({ ...manualForm, shop: e.target.value })}
                    />
                  </div>

                  {connectionMethod === 'token' ? (
                    <div className="space-y-2">
                      <Label htmlFor="accessToken">Access Token *</Label>
                      <Input
                        id="accessToken"
                        type="password"
                        placeholder="shpat_..."
                        value={manualForm.accessToken}
                        onChange={(e) => setManualForm({ ...manualForm, accessToken: e.target.value })}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="clientId">Client ID *</Label>
                        <Input
                          id="clientId"
                          placeholder="Shoipify Client ID"
                          value={manualForm.clientId}
                          onChange={(e) => setManualForm({ ...manualForm, clientId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientSecret">Client Secret *</Label>
                        <Input
                          id="clientSecret"
                          type="password"
                          placeholder="Shopify Client Secret"
                          value={manualForm.clientSecret}
                          onChange={(e) => setManualForm({ ...manualForm, clientSecret: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>

                <p className="text-sm text-muted-foreground">
                  API version defaults to 2025-01. Plan and currency can be updated later.
                </p>

                {connectionMethod === 'token' && (
                  <p className="text-xs text-muted-foreground">
                    Need credentials? In Shopify admin go to <span className="font-medium">Settings → Apps &amp; sales channels → Develop apps</span>,
                    create (or select) a custom app, then add the <span className="font-medium">read_products</span>, <span className="font-medium">write_products</span>,
                    <span className="font-medium">read_orders</span>, <span className="font-medium">write_orders</span>, <span className="font-medium">read_customers</span>, <span className="font-medium">write_customers</span>,
                    <span className="font-medium">read_checkouts</span>, <span className="font-medium">read_inventory</span>, and <span className="font-medium">write_inventory</span> scopes to generate an Admin API access token.
                  </p>
                )}

                {connectionMethod === 'credentials' && (
                  <p className="text-xs text-muted-foreground">
                    Use <strong>Client Credentials</strong> for automatic token refreshing.
                    Tokens generated this way expire in 24h, but we will auto-refresh them for you.
                  </p>
                )}

                <Button size="sm" onClick={handleManualConnect} disabled={manualLoading}>
                  {manualLoading ? 'Connecting…' : 'Connect Manually'}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-4 pt-4">
          {/* Actions bar */}
          <div className="flex items-center gap-2">
            <Input
              value={getTopicUrl('__default__')}
              onChange={(e) => setTopicUrl('__default__', e.target.value)}
              placeholder="https://your-backend.com/api/v1/shopify/webhooks"
              className="flex-1 font-mono text-sm h-9"
            />
            <Button
              size="sm"
              onClick={handleRegisterAll}
              disabled={registerAllLoading || webhooksLoading || !getTopicUrl('__default__')}
              className="bg-[#95BF47] hover:bg-[#7da03b] text-white h-9 shrink-0"
            >
              {registerAllLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Webhook className="h-4 w-4 mr-1.5" />}
              Register All
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={loadWebhooks} disabled={webhooksLoading}>
              <RefreshCw className={`h-4 w-4 ${webhooksLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Webhook table */}
          <div className="rounded-lg border overflow-hidden">
            {webhooksLoading && webhooks.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading webhooks from Shopify...
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b">
                    <th className="text-left font-medium text-muted-foreground text-xs px-4 py-2.5 w-10">Status</th>
                    <th className="text-left font-medium text-muted-foreground text-xs px-4 py-2.5">Event</th>
                    <th className="text-left font-medium text-muted-foreground text-xs px-4 py-2.5">Webhook URL</th>
                    <th className="text-right font-medium text-muted-foreground text-xs px-4 py-2.5 w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {SUPPORTED_WEBHOOK_TOPICS.map(({ topic, label }) => {
                    const registered = webhooks.find(w => w.topic === topic);
                    const isCreating = webhookCreating === topic;
                    const isDeleting = registered ? webhookDeleting === registered.id : false;
                    const isEditing = editingTopic === topic;
                    const currentUrl = getTopicUrl(topic);
                    const urlChanged = registered && currentUrl !== registered.address;

                    return (
                      <tr key={topic} className="hover:bg-muted/20 transition-colors">
                        {/* Status */}
                        <td className="px-4 py-2.5">
                          <div className={`w-2.5 h-2.5 rounded-full mx-auto ${registered ? 'bg-green-500' : 'bg-gray-300'}`} />
                        </td>
                        {/* Event */}
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-sm">{label}</div>
                          <code className="text-[10px] font-mono text-muted-foreground">{topic}</code>
                        </td>
                        {/* URL */}
                        <td className="px-4 py-2.5">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                autoFocus
                                value={currentUrl}
                                onChange={(e) => setTopicUrl(topic, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    setEditingTopic(null);
                                    if (registered && urlChanged) handleUpdateWebhook(topic, registered.id);
                                    else if (!registered) handleCreateWebhook(topic);
                                  }
                                  if (e.key === 'Escape') setEditingTopic(null);
                                }}
                                placeholder="https://..."
                                className="font-mono text-xs h-7 flex-1"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0 text-green-600 hover:text-green-700"
                                onClick={() => {
                                  setEditingTopic(null);
                                  if (registered && urlChanged) handleUpdateWebhook(topic, registered.id);
                                  else if (!registered) handleCreateWebhook(topic);
                                }}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <span
                              className="font-mono text-xs text-muted-foreground truncate block max-w-md cursor-pointer hover:text-foreground transition-colors"
                              onClick={() => setEditingTopic(topic)}
                              title={registered ? registered.address : 'Click to set URL'}
                            >
                              {registered ? registered.address : <span className="italic text-muted-foreground/60">Not registered</span>}
                            </span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {registered ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setEditingTopic(editingTopic === topic ? null : topic)}
                                  title="Edit URL"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteWebhook(registered.id)}
                                  disabled={isDeleting}
                                  title="Delete webhook"
                                >
                                  {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleCreateWebhook(topic)}
                                disabled={isCreating}
                              >
                                {isCreating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                Register
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Extra webhooks not in our supported list */}
                  {webhooks.filter(w => !SUPPORTED_WEBHOOK_TOPICS.some(t => t.topic === w.topic)).map(w => (
                    <tr key={w.id} className="bg-muted/10 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="w-2.5 h-2.5 rounded-full mx-auto bg-blue-400" />
                      </td>
                      <td className="px-4 py-2.5">
                        <code className="text-xs font-mono">{w.topic}</code>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-xs text-muted-foreground truncate block max-w-md">{w.address}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteWebhook(w.id)}
                            disabled={webhookDeleting === w.id}
                          >
                            {webhookDeleting === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4 pt-4">
          <ShopifyProducts
            selectedAccountId={selectedAccountId}
            isConnected={isConnected}
            shopCurrency={shopCurrency}
            refreshKey={productsRefreshKey}
            hideToolbar={true}
          />
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4 pt-4">
          <ShopifyCustomers
            selectedAccountId={selectedAccountId}
            isConnected={isConnected}
            refreshKey={customersRefreshKey}
          />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4 pt-4">
          <ShopifyOrders
            selectedAccountId={selectedAccountId}
            isConnected={isConnected}
            shopCurrency={shopCurrency}
            shopDomain={connectionInfo?.shop}
            refreshKey={ordersRefreshKey}
          />
        </TabsContent>

        {/* Abandoned Checkouts Tab */}
        {/* Abandoned Checkouts Tab */}
        <TabsContent value="checkouts" className="space-y-4 pt-4">
          <ShopifyCheckouts
            selectedAccountId={selectedAccountId}
            isConnected={isConnected}
            shopCurrency={shopCurrency}
            refreshKey={checkoutsRefreshKey}
          />
        </TabsContent>

        {/* <TabsContent value="api-keys" className="space-y-4 pt-4">
          <ApiKeysManager />
        </TabsContent> */}

        {/* Playbooks Tab */}
        <TabsContent value="playbooks" className="space-y-4 pt-4">
          <ShopifyPlaybooks
            businessAccountId={selectedAccountId || ''}
            isConnected={isConnected}
          />
        </TabsContent>
      </Tabs >

    </div >
  );
}
